/** ------------------ Primary Import Declaration ------- */
import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
/** ------------------ MUI Components & APIs ------------ */
import {
  Box,
  Grid,
  Button,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
/** ------------------ Media Imports --------------------- */
/** ------------------ Icons ----------------------------- */
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import KeyIcon from "@mui/icons-material/Key";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import PersonIcon from "@mui/icons-material/Person";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MessageIcon from "@mui/icons-material/Message";
import StorefrontIcon from "@mui/icons-material/Storefront";
/** ------------------ Style Components ------------------ */
/** ------------------ User Components ------------------- */
import SnackBar from "CommonComponents/Snackbar/SnackBar.jsx";
import {
  onlyNumbers,
  onlyPassword,
  onlyEmail,
} from "commonmethods/Validatations";
import { postRecord } from "services/services";
import { AuthContext } from "context/Authentication";
import {
  getReturnUrlFromLocation,
  createLoginUrlWithReturn,
} from "utils/authUtils";
/** ------------------ API Declarations ------------------ */
const API_Registration = "users/register";
const API_VendorRegistration = "users/registervendor";
/** ------------------ Third Party Components ------------ */

export default function AuthRegistration(props) {
  let navigate = useNavigate();
  const location = useLocation();
  const context = useContext(AuthContext);
  /** ------------------ Initial States -------------------- */
  const initialRegistrationState = {
    User_Email: "",
    User_Password: "",
    Confirm_Password: "",
  };
  /** ------------------ useStates ------------------------- */
  const { dispatch } = useContext(AuthContext);
  const [thisRegistration, setThisRegistration] = useState(
    initialRegistrationState
  );
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    color: "success",
    message: "Hi",
  });
  const [isVendor, setIsVendor] = useState(false);
  /** ------------------ useEffects ------------------------ */
  useEffect(() => {
    if (thisRegistration.User_Password === thisRegistration.Confirm_Password) {
      setPasswordMatch(true);
    } else {
      setPasswordMatch(false);
    }
  }, [thisRegistration]);
  /** ------------------ Functions & Events ---------------- */
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setThisRegistration({ ...thisRegistration, [name]: value });
  };
  const handleLoginRedirect = (user) => {
    navigate(user.Default_Page);
  };
  const handleSubmit = () => {
    setLoading(true); // Start loading state
    var objData = {
      User_Email: thisRegistration.User_Email,
      User_Password: thisRegistration.User_Password,
    };
    // Use the appropriate API endpoint based on the vendor checkbox
    const apiEndpoint = isVendor ? API_VendorRegistration : API_Registration;

    // Make the API call using postRecord
    postRecord(apiEndpoint, objData)
      .then((response) => {
        // Ensure the API call was successful
        if (response.status === 200 && response.data) {
          // Parse the response data
          var result =
            response.data?.Response_Data &&
            JSON.parse(response.data?.Response_Data);

          setSnackOptions({
            color: result?.Color || "success",
            message:
              result?.Message || "Registration successful! Logging you in...",
            icon: result?.Icon || "CheckCircleIcon",
          });
          setSnackOpen(true);
          setLoading(false);

          // Auto-login the user since the API returns session data
          const userData = response.data;
          const Permissions = JSON.parse(userData.User_Permission);
          const Token = userData.Session_Token;

          // Filter out sensitive data from user object
          const {
            User_Permission,
            Response_Data,
            Session_Token,
            Refresh_Token,
            ...filteredUserData
          } = userData;

          // Dispatch login action to update auth context
          dispatch({
            type: "LOGIN",
            payload: {
              user: filteredUserData,
              permissions: Permissions,
              token: Token,
            },
          });

          // Navigate to return URL or default page after a short delay
          setTimeout(() => {
            const returnUrl = getReturnUrlFromLocation(location);
            if (returnUrl) {
              navigate(returnUrl);
            } else {
              navigate(userData.Default_Page);
            }
          }, 1500);
        }
      })
      .catch((error) => {
        // Handle API or network errors
        if (error.response) {
          // Use error message from backend
          setSnackOptions({
            color: error.response.data?.Color || "error",
            message:
              error.response.data?.detail ||
              "Something went wrong during registration.",
            icon: error.response.data?.Icon || "ErrorOutlineOutlinedIcon",
          });
        } else {
          // Handle network or unexpected errors
          setSnackOptions({
            color: "error",
            message: error.message || "Network error. Please try again.",
            icon: "ErrorOutlineOutlinedIcon",
          });
        }
        setSnackOpen(true);
        setLoading(false); // Stop loading
      });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  return (
    <React.Fragment>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Box sx={{ mt: 1, alignItems: "center" }}>
          {context.state?.companyInfo?.Is_Aggregator === "1" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isVendor}
                  onChange={(e) => setIsVendor(e.target.checked)}
                  color="primary"
                  icon={<StorefrontIcon color="disabled" />}
                  checkedIcon={<StorefrontIcon color="primary" />}
                />
              }
              label="I am a vendor"
            />
          )}
          <TextField
            margin="normal"
            required
            fullWidth
            placeholder="Email"
            type="email"
            name="User_Email"
            onChange={handleInputChange}
            value={thisRegistration.User_Email}
            onInput={(e) => onlyEmail(e)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            required
            margin="normal"
            fullWidth
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            name="User_Password"
            onChange={handleInputChange}
            onInput={(e) => onlyPassword(e)}
            value={thisRegistration.User_Password}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Password must contain at least 8 characters, one uppercase and one lowercase character, one number and a special character"
          />

          <TextField
            required
            margin="normal"
            fullWidth
            type={showPassword ? "text" : "password"}
            placeholder="Confirm Password"
            name="Confirm_Password"
            value={thisRegistration.Confirm_Password}
            onChange={handleInputChange}
            onInput={(e) => onlyPassword(e)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    color={passwordMatch ? "success" : "error"}
                  >
                    {passwordMatch ? (
                      <CheckCircleOutlinedIcon />
                    ) : (
                      <CancelOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Before submit, Please match the password..."
          />
        </Box>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <MessageIcon />}
        >
          Submit
        </Button>
        <Button
          type="reset"
          fullWidth
          variant="text"
          color="info"
          onClick={() => {
            const currentPath = location.pathname + location.search;
            const loginUrl = createLoginUrlWithReturn(
              getReturnUrlFromLocation(location) || currentPath
            );
            navigate(loginUrl);
          }}
        >
          Already a member? SignIn
        </Button>
      </form>
      <SnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        options={snackOptions}
      />
    </React.Fragment>
  );
}
